import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Image as ImageIcon } from "lucide-react";
import { api, API, getToken } from "../../../lib/auth";

function uploadFile(file, kind = "banner") {
  const fd = new FormData();
  fd.append("kind", kind);
  fd.append("file", file);
  return fetch(`${API}/t/store/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  }).then((r) => r.json());
}

function CRUDPage({ kind, title, breadcrumb, fields, endpoint, testidBase }) {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);

  const load = () => api.get(endpoint).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name || r.title}"?`)) return;
    await api.delete(`${endpoint}/${r.id}`);
    toast.success("Deleted");
    load();
  };

  const save = async () => {
    const payload = { ...dlg };
    delete payload._editing;
    delete payload.id;
    delete payload.created_at;
    delete payload.tenant_id;
    try {
      if (dlg._editing) {
        await api.patch(`${endpoint}/${dlg._editing}`, payload);
        toast.success("Updated");
      } else {
        await api.post(endpoint, payload);
        toast.success("Created");
      }
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid={`${testidBase}-page`} className="p-6 lg:p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">{title}</h1>
          <div className="text-sm text-black/55 mt-1">{breadcrumb}</div>
        </div>
        <button onClick={() => setDlg({ ...fields.reduce((a, f) => ({ ...a, [f.k]: f.default ?? "" }), {}), active: true })}
          data-testid={`${testidBase}-new`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-sm font-bold hover:opacity-95">
          <Plus size={14} /> New
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-black/5 p-5 group">
            {r.image_url && (
              <div className="rounded-xl overflow-hidden mb-3 aspect-video bg-[#F4F5FA]">
                <img
  src={
    r.image_url?.startsWith("http")
      ? r.image_url
      : `${process.env.REACT_APP_BACKEND_URL}${r.image_url}`
  }
  alt=""
  className="w-full h-full object-cover"
/>
              </div>
            )}
            <div className="font-bold text-black">{r.name || r.title}</div>
            <div className="text-xs text-black/55 mt-1 line-clamp-2">{r.description || r.subtitle}</div>
            <div className="mt-3 flex items-center justify-between">
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-black/5 text-black/55"}`}>
                {r.active ? "Active" : "Inactive"}
              </span>
              <div className="flex gap-1.5">
                <button onClick={() => setDlg({ ...r, _editing: r.id })} data-testid={`${testidBase}-edit-${r.id}`}
                  className="p-2 rounded-md bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D1F0DA]"><Pencil size={13} /></button>
                <button onClick={() => remove(r)} data-testid={`${testidBase}-delete-${r.id}`}
                  className="p-2 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-black/5 p-10 text-center text-black/45">
            <ImageIcon size={32} className="mx-auto mb-3 opacity-40" />
            No {title.toLowerCase()} yet.
          </div>
        )}
      </div>

      {dlg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDlg(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-black/8 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl text-black">{dlg._editing ? "Edit" : "New"} {title.replace(/s$/, "")}</h2>
              <button onClick={() => setDlg(null)} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              {fields.map((f) => (
                <div key={f.k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-black/55">{f.lbl}</label>
                  {f.type === "textarea" ? (
                    <textarea value={dlg[f.k] || ""} onChange={(e) => setDlg({ ...dlg, [f.k]: e.target.value })} rows={3}
                      data-testid={`${testidBase}-field-${f.k}`}
                      className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
                  ) : f.type === "image" ? (
                    <div className="mt-1.5 space-y-2">
                      <input type="file" accept="image/*"
                        data-testid={`${testidBase}-field-${f.k}`}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const j = await uploadFile(file, kind);
                          if (j.url) setDlg({ ...dlg, [f.k]: j.url });
                        }}
                        className="block w-full text-sm" />
                      {dlg[f.k] && (
  <img
    src={
      dlg[f.k]?.startsWith("http")
        ? dlg[f.k]
        : `${process.env.REACT_APP_BACKEND_URL}${dlg[f.k]}`
    }
    alt=""
    className="w-full h-32 object-cover rounded-lg"
  />
)}
                    </div>
                  ) : f.type === "number" ? (
                    <input type="number" value={dlg[f.k] || 0} onChange={(e) => setDlg({ ...dlg, [f.k]: Number(e.target.value) })}
                      data-testid={`${testidBase}-field-${f.k}`}
                      className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
                  ) : (
                    <input value={dlg[f.k] || ""} onChange={(e) => setDlg({ ...dlg, [f.k]: e.target.value })}
                      data-testid={`${testidBase}-field-${f.k}`}
                      className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
                  )}
                </div>
              ))}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={dlg.active !== false} onChange={(e) => setDlg({ ...dlg, active: e.target.checked })}
                  data-testid={`${testidBase}-field-active`}
                  className="w-4 h-4 accent-[#1FA868]" />
                <span className="text-sm font-semibold text-black">Active</span>
              </label>
            </div>
            <div className="p-5 border-t border-black/8 flex justify-end gap-2">
              <button onClick={() => setDlg(null)} className="px-4 py-2 rounded-lg border border-black/15 text-sm font-semibold">Cancel</button>
              <button onClick={save} data-testid={`${testidBase}-save`}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-sm font-bold">
                {dlg._editing ? "Save changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function StoreCollections() {
  return <CRUDPage
    kind="collection" testidBase="collections"
    title="Collections" breadcrumb="Store · Collections"
    endpoint="/t/store/collections"
    fields={[
      { k: "name", lbl: "Name" },
      { k: "description", lbl: "Description", type: "textarea" },
      { k: "image_url", lbl: "Cover image", type: "image" },
    ]}
  />;
}

export function StoreBanners() {
  return <CRUDPage
    kind="banner" testidBase="banners"
    title="Banners" breadcrumb="Store · Banners"
    endpoint="/t/store/banners"
    fields={[
      { k: "title", lbl: "Title" },
      { k: "subtitle", lbl: "Subtitle", type: "textarea" },
      { k: "link_url", lbl: "Link URL" },
      { k: "image_url", lbl: "Image", type: "image" },
      { k: "position", lbl: "Position", type: "number", default: 0 },
    ]}
  />;
}
